import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useConfig } from "../../hooks/useConfig"
import { useParticipant } from "../../hooks/useParticipant"
import { useBoothSession } from "../../hooks/useBoothSession"
import CameraView from "../views/CameraView"
import CameraControls from "../common/CameraControls"
import CropperModal from "../modals/CropperModal"
import HeadshotTipsModal from "../modals/HeadshotTipsModal"
import SwitchDeviceModal from "../modals/SwitchDeviceModal"
import SizeRejectionModal from "../modals/SizeRejectionModal"
import { checkImageSize, cropImage, processImageFile } from "../../utils/ImageUtil"
import { Area } from "react-easy-crop/types"
import { Fade } from "@mui/material"

function CameraPage() {
  const navigate = useNavigate()

  const config = useConfig()
  const { participant } = useParticipant(config.presetCode)
  const { rawImage, setRawImage } = useBoothSession()

  const [cameraOptions, setCameraOptions] = useState<MediaDeviceInfo[]>([])
  const [selectedCameraIndex, setSelectedCameraIndex] = useState(0)
  const [didStartCapture, setDidStartCapture] = useState(false)
  const [finishedCountdown, setFinishedCountdown] = useState(false)
  const [showHeadshotTips, setShowHeadshotTips] = useState(false)
  const [hasShownHeadshotTips, setHasShownHeadshotTips] = useState(false)
  const [showSwitchDevice, setShowSwitchDevice] = useState(false)
  const [showSizeRejection, setShowSizeRejection] = useState(false)
  const [imageToCrop, setImageToCrop] = useState<string | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)

  const sharedCanvas = useRef<HTMLCanvasElement>(null)
  const cameraErrorRef = useRef(cameraError)
  cameraErrorRef.current = cameraError

  useEffect(() => {
    setRawImage(null)
  }, [setRawImage])

  useEffect(() => {
    setShowHeadshotTips(config.headshots.showTips)
  }, [config])

  const handleCapturedImage = useCallback(
    (image: string, source: string) => {
      checkImageSize(image, config.headshots.minimumSize, (acceptable) => {
        if (acceptable) {
          setRawImage(image)
          setImageToCrop(null)
          setDidStartCapture(false)
          if (config.aiEffectOn) {
            const guestInputs = config.aiEffectSequence?._embedded.guestInputs ?? []
            if (guestInputs.length > 0) {
              navigate(`/${config.presetCode}/guest-input`)
            } else {
              navigate(`/${config.presetCode}/photo-processing`)
            }
          } else if (config.backgroundReplacementOn) {
            navigate(`/${config.presetCode}/photo-processing`)
          } else {
            navigate(`/${config.presetCode}/edit`)
          }
        } else {
          setShowSizeRejection(true)
          setRawImage(null)
          setDidStartCapture(false)
        }
      })
    },
    [config, navigate, setRawImage],
  )

  const handleCameraError = useCallback(
    (error: string) => {
      if (cameraErrorRef.current) return
      setCameraError(error)
      navigate(`/${config.presetCode}/camera-fallback`)
    },
    [config, navigate],
  )

  const showImageCropper = useCallback(
    (imageFile: File) => {
      processImageFile(imageFile, (imageData: string) => {
        checkImageSize(imageData, config.headshots.minimumSize, (acceptable) => {
          if (acceptable) {
            setImageToCrop(imageData)
          } else {
            setShowSizeRejection(true)
          }
        })
      })
    },
    [config],
  )

  const cancelImageCrop = useCallback(() => {
    setImageToCrop(null)
  }, [])

  const finishCrop = useCallback(
    (cropRect: Area, imageData: string) => {
      const image = new Image()
      image.src = imageData

      const canvas = sharedCanvas.current
      if (!canvas) {
        throw new Error("Canvas missing")
      }

      handleCapturedImage(cropImage(image, canvas, cropRect, false, config.aspectRatio), "camera-fallback")
    },
    [handleCapturedImage],
  )

  const switchCamera = useCallback(() => {
    setSelectedCameraIndex((index) => {
      const newIndex = index + 1
      return newIndex > cameraOptions.length - 1 ? 0 : newIndex
    })
  }, [cameraOptions.length])

  const theme = config.theme

  const webcamReady = cameraOptions.length > 0
  const showCameraControls = !didStartCapture && imageToCrop == null && rawImage == null
  return (
    <>
      <canvas
        className="hiddenCanvas"
        ref={sharedCanvas}
      ></canvas>

      {imageToCrop && (
        <CropperModal
          show={rawImage == null}
          showFaceGuide={config.headshots.showFaceGuide}
          onClose={cancelImageCrop}
          image={imageToCrop}
          finishCrop={finishCrop}
        />
      )}

      <SwitchDeviceModal
        show={showSwitchDevice}
        participant={participant}
        onHide={() => setShowSwitchDevice(false)}
      />

      {cameraOptions.length > 0 && !hasShownHeadshotTips && (
        <HeadshotTipsModal
          show={showHeadshotTips}
          onHide={() => {
            setShowHeadshotTips(false)
            setHasShownHeadshotTips(true)
          }}
        />
      )}

      <CameraView
        theme={theme}
        startCapture={didStartCapture}
        selectedCamera={cameraOptions.length > 0 ? cameraOptions[selectedCameraIndex] : null}
        showFaceGuide={config.headshots.showFaceGuide}
        showCameraButton={showCameraControls && webcamReady}
        onStartCapture={() => setDidStartCapture(true)}
        finishCountdown={() => setFinishedCountdown(true)}
        postDelay={900}
        handleDevices={(devices: MediaDeviceInfo[]) => setCameraOptions(devices)}
        handleImage={(image, source) => handleCapturedImage(image, source)}
        handleError={(error) => handleCameraError(error)}
      />

      {showCameraControls && (
        <CameraControls
          theme={theme}
          canSwitchCamera={cameraOptions.length > 1}
          switchCamera={() => switchCamera()}
          switchDevice={() => setShowSwitchDevice(true)}
          handleUserSelectedImage={(imageFile) => showImageCropper(imageFile)}
        />
      )}

      {showSizeRejection && (
        <SizeRejectionModal
          show={showSizeRejection}
          minWidth={config.headshots.minimumSize.width}
          minHeight={config.headshots.minimumSize.height}
          onHide={() => setShowSizeRejection(false)}
        />
      )}

      <Fade
        in={didStartCapture && !finishedCountdown}
        timeout={500}
      >
        <div className="countdownFade"></div>
      </Fade>

    </>
  )
}

export default CameraPage
